import { Outlet, Navigate, useLocation } from "react-router-dom";
import { isHiddenRoute } from "@/lib/hiddenRoutes";
import { useCompany } from "@/contexts/CompanyContext";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { AppSubHeader } from "./AppSubHeader";
import { BackToProvider } from "./BackToContext";
import { DemoCompanyBanner } from "./DemoCompanyBanner";
import { AIAdvisorFAB } from "@/components/ai/AIAdvisorFAB";
import { SubscriptionGuard } from "@/components/subscription/SubscriptionGuard";
import { TrialBanner } from "@/components/subscription/TrialBanner";
import { PrintErrorBoundary } from "@/components/print/PrintErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { RoutePermissionGate } from "@/components/auth/RoutePermissionGate";
import { PlanRouteGate } from "@/components/subscription/PlanRouteGate";
import { ReportViewTracker } from "@/components/reports/ReportViewTracker";
import { useSubscriptionPaymentSync } from "@/hooks/useSubscriptionPayments";
import { Loader2 } from "lucide-react";
import { PerfOverlay } from "@/components/PerfOverlay";

export const AppLayout = () => {
  const { companies, currentCompany, loading } = useCompany();
  const { pathname } = useLocation();
  useSubscriptionPaymentSync();

  if (loading) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (companies.length === 0 || !currentCompany) return <Navigate to="/companies" replace />;

  if (isHiddenRoute(pathname)) return <Navigate to="/dashboard" replace />;

  const isReport = pathname.startsWith("/reports");

  return (
    <BackToProvider>
    <div className="min-h-screen flex bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar />
        <AppSubHeader />
        <div className="print:hidden"><TrialBanner /></div>
        <div className="print:hidden"><DemoCompanyBanner /></div>
        <main className={`flex-1 p-3 sm:p-4 md:p-6 pb-20 ${isReport ? "report-page overflow-x-visible" : "overflow-x-clip"}`}>
          <PrintErrorBoundary>
            <SubscriptionGuard>
              <RouteErrorBoundary>
                <RoutePermissionGate><PlanRouteGate><Outlet /></PlanRouteGate></RoutePermissionGate>
              </RouteErrorBoundary>
            </SubscriptionGuard>
          </PrintErrorBoundary>
        </main>
      </div>
      
      <AIAdvisorFAB />
      <ReportViewTracker />
      <PerfOverlay />
    </div>
    </BackToProvider>
  );
};
